import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, MapPinIcon, RadioIcon, ThumbsUpIcon, UsersIcon } from 'lucide-react';
import { profileCoordinates, publicReportStatusLabels, publicReports, reportCategories, type PublicReport, type PublicReportCategory } from '../../data/demandes';
type Filter = 'tous' | PublicReportCategory;
const filters: Filter[] = ['tous', 'danger', 'voirie', 'eclairage', 'arbre'];

/** Bleu de nuit des armoiries, pour le marqueur du domicile. */
const HOME_COLOR = '#0f2450';
const statusStyles: Record<PublicReport['status'], string> = {
  nouveau: 'bg-ville-lime text-ville-navy',
  'en-traitement': 'bg-ville-indigo-soft text-ville-indigo',
  resolue: 'bg-slate-100 text-slate-500'
};
function pinIcon(report: PublicReport, active: boolean): L.DivIcon {
  const color = reportCategories[report.category].color;
  const size = active ? 34 : 24;
  return L.divIcon({
    className: 'atna-pin',
    html: `<span style="background:${color};width:${size}px;height:${size}px" class="block rounded-full border-[3px] border-white shadow-md shadow-ville-navy/30 ${active ? 'ring-2 ring-ville-navy/20' : ''}"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
export function PublicReportsMap() {
  const [filter, setFilter] = useState<Filter>('tous');
  const [selectedId, setSelectedId] = useState<string>(publicReports[0].id);
  const [confirmedIds, setConfirmedIds] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const visible = useMemo(() => filter === 'tous' ? publicReports : publicReports.filter(report => report.category === filter), [filter]);
  const selected = visible.find(report => report.id === selectedId) ?? visible[0];
  const selectedIndex = selected ? visible.indexOf(selected) : -1;
  const newCount = publicReports.filter(report => report.status === 'nouveau').length;
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: profileCoordinates,
      zoom: 14,
      zoomControl: false,
      scrollWheelZoom: false,
      attributionControl: true
    });
    map.attributionControl.setPrefix(false);
    // Tuiles OpenStreetMap : gratuites et sans clé d'API.
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map);
    L.control.zoom({
      position: 'topright'
    }).addTo(map);
    L.circleMarker(profileCoordinates, {
      radius: 7,
      color: '#ffffff',
      weight: 3,
      fillColor: HOME_COLOR,
      fillOpacity: 1
    }).bindTooltip('Votre adresse').addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    visible.forEach(report => {
      L.marker([report.lat, report.lng], {
        icon: pinIcon(report, report.id === selected?.id),
        keyboard: true,
        title: `${reportCategories[report.category].label} — ${report.title}`,
        alt: `${reportCategories[report.category].label} — ${report.title}`,
        zIndexOffset: report.id === selected?.id ? 500 : 0
      }).on('click', () => setSelectedId(report.id)).on('keypress', () => setSelectedId(report.id)).addTo(layer);
    });
  }, [visible, selected?.id]);
  useEffect(() => {
    if (!mapRef.current || !selected) return;
    mapRef.current.panTo([selected.lat, selected.lng], {
      animate: true,
      duration: 0.3
    });
  }, [selected?.id]);
  const step = (direction: -1 | 1) => {
    if (visible.length === 0) return;
    const next = (selectedIndex + direction + visible.length) % visible.length;
    setSelectedId(visible[next].id);
  };
  const toggleConfirm = (id: string) => {
    setConfirmedIds(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  };
  return <section aria-labelledby="carte-publique-titre"
  /* `isolate` garde les couches Leaflet (contrôles, fiche flottante) dans leur propre
     contexte d'empilement, sinon elles passent au-dessus du dialogue ATNA. */ className="relative isolate z-0 overflow-hidden rounded-3xl border border-slate-200 bg-white" data-id="element-963" data-mp="2bisb07ehrgc4">
      <div className="px-5 pb-4 pt-5 sm:px-6" data-id="element-964" data-mp="1wkzxtlwf3413">
        <div className="flex items-start justify-between gap-4" data-id="element-965" data-mp="1sjmqf7doess2">
          <div data-id="element-966" data-mp="1sl7o35vaeru5">
            <h2 id="carte-publique-titre" className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-967" data-mp="3qyr1m4s2etom">
              Signalements autour de vous
            </h2>
            <p className="mt-1 text-sm text-slate-500" data-id="element-968" data-mp="3mxxe3cijbgw9">
              {publicReports.length} signalements actifs · {newCount} nouveaux aujourd’hui
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ville-mist px-3 py-1 text-xs font-bold text-ville-deep" data-id="element-969" data-mp="1dn6h94emth0n">
            <RadioIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-970" data-mp="1ihk6sq6m4txe" />
            En direct
          </span>
        </div>

        <ul className="mt-4 flex flex-wrap gap-2" data-id="element-971" data-mp="pqsfo74emvgx">
          {filters.map(option => {
          const active = filter === option;
          const label = option === 'tous' ? 'Tous' : reportCategories[option].label;
          return <li key={option} data-id="element-972" data-mp="ypnkbersvxi4">
                <button type="button" onClick={() => setFilter(option)} aria-pressed={active} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-navy bg-ville-navy text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-973" data-mp="ytjodz5wbd4v">
                  {option !== 'tous' && <span className="h-2 w-2 rounded-full" style={{
                backgroundColor: reportCategories[option].color
              }} aria-hidden="true" data-id="element-974" data-mp="k33gm4fzna46" />}
                  {label}
                </button>
              </li>;
        })}
        </ul>
      </div>

      <div className="relative" data-id="element-975" data-mp="1bj040t4qs74">
        <div ref={containerRef} role="application" aria-label="Carte des signalements publics à Baie-Trinité" className="h-[300px] w-full bg-ville-mist sm:h-[380px]" data-id="element-976" data-mp="1ahq7znuobqz" />

        {selected ? <motion.div key={selected.id} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto absolute inset-x-3 bottom-3 z-[900] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-ville-navy/10 sm:inset-x-4" data-id="element-977" data-mp="1kaqpyiz58f7f">
            <div className="flex items-start gap-3" data-id="element-978" data-mp="230yn8kg5wbfp">
              <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{
            backgroundColor: reportCategories[selected.category].color
          }} aria-hidden="true" data-id="element-979" data-mp="231hv18c3ljg0" />
              <div className="min-w-0 flex-1" data-id="element-980" data-mp="1u2eby4cxl41i">
                <div className="flex flex-wrap items-center gap-2" data-id="element-981" data-mp="1yylju2ow0pm3">
                  <h3 className="text-[15px] font-extrabold leading-tight text-ville-navy" data-id="element-982" data-mp="il4wdswxaw5h">{selected.title}</h3>
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${statusStyles[selected.status]}`} data-id="element-983" data-mp="njy6dbd0450w">
                    {publicReportStatusLabels[selected.status]}
                  </span>
                </div>
                <p className="mt-1 flex items-center gap-1.5 text-[13px] text-slate-500" data-id="element-984" data-mp="1a7wsilrv9znf">
                  <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-985" data-mp="15ao96huzfpkl" />
                  <span className="truncate" data-id="element-986" data-mp="10b1t80nrrv83">{selected.address}</span>
                </p>
                <p className="mt-0.5 text-[13px] text-slate-400" data-id="element-987" data-mp="1xy0vcqx8dvdd">
                  À {selected.distance} · {selected.reportedAgo}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1" data-id="element-988" data-mp="1t1adpz5s2rpu">
                <button type="button" onClick={() => step(-1)} aria-label="Signalement précédent" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-989" data-mp="1o767ji4c6npt">
                  <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-990" data-mp="2jb14x1xkw1lh" />
                </button>
                <span className="text-[11px] font-semibold tabular-nums text-slate-400" data-id="element-991" data-mp="2o8jgz16w6wxp">
                  {selectedIndex + 1}/{visible.length}
                </span>
                <button type="button" onClick={() => step(1)} aria-label="Signalement suivant" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-992" data-mp="2y7iebnm7j65e">
                  <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-993" data-mp="3gvneym7kiazz" />
                </button>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3" data-id="element-994" data-mp="3lu8fynun8yph">
              <p className="flex items-center gap-1.5 text-[13px] text-slate-500" data-id="element-995" data-mp="2vp82ksad7d9a">
                <UsersIcon className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-996" data-mp="2vn54dkz26dth" />
                {selected.confirmations + (confirmedIds.includes(selected.id) ? 1 : 0)} personnes le confirment
              </p>
              <button type="button" onClick={() => toggleConfirm(selected.id)} aria-pressed={confirmedIds.includes(selected.id)} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 ${confirmedIds.includes(selected.id) ? 'bg-ville-indigo-soft text-ville-indigo' : 'bg-ville-navy text-white hover:bg-ville-navy/90'}`} data-id="element-997" data-mp="3ae474fd7q855">
                <ThumbsUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-998" data-mp="1z00htmhkl86f" />
                {confirmedIds.includes(selected.id) ? 'Confirmé' : 'Je confirme'}
              </button>
            </div>
          </motion.div> : <div className="absolute inset-x-3 bottom-3 z-[900] rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-xl shadow-ville-navy/10" data-id="element-999" data-mp="1mnetyby3mj2c">
            Aucun signalement dans cette catégorie pour le moment.
          </div>}
      </div>
    </section>;
}